import { createSelector } from "@reduxjs/toolkit";
import { format, isAfter } from "date-fns";
import { RootState } from "reduxUtils/store";

/**
 * Select the current rates based on the current contract and time
 */
export const isUserGstRegistered = createSelector(
  (state: RootState) => state.billingEntityData?.billingEntityData,
  (billingEnityData) => {
    if (!billingEnityData) return false;

    return Boolean(billingEnityData.gst_registration);
  },
);

/**
 * Select the billing entity data start date (or today's date)
 */
export const selectBillingEntityDataStartAtOrToday = createSelector(
  (state: RootState) => state.billingEntityData?.billingEntityData?.start_at,
  (billingEntityDataStartAt) =>
    billingEntityDataStartAt ? new Date(billingEntityDataStartAt) : new Date(),
);

/**
 * Select the billing entity data end date (or today's date)
 */
export const selectBillingEntityDataEndAtOrToday = createSelector(
  (state: RootState) => state.billingEntityData?.billingEntityData?.end_at,
  (billingEntityDataEndAt) =>
    billingEntityDataEndAt ? new Date(billingEntityDataEndAt) : new Date(),
);

//---------------- Formatted versions of the above selectors ------------------ //

/**
 * Select the billing entity data start/end date (or today's date)
 * formatted String as dd/MM/yyyy
 */
export const selectTextForBillingEntityDateRange = createSelector(
  selectBillingEntityDataStartAtOrToday,
  selectBillingEntityDataEndAtOrToday,
  (billingEntityDataStartAtOrToday, billingEntityDataEndAtOrToday) => {
    return {
      startAtDate: format(billingEntityDataStartAtOrToday, "dd/MM/yyyy"),
      endAtDate: format(billingEntityDataEndAtOrToday, "dd/MM/yyyy"),
    };
  },
);

/**
 * Select the billing entity data start/end date (or today's date)
 * formatted String as yyyy-MM-dd
 */
export const selectInputForBillingEntityDateRange = createSelector(
  selectBillingEntityDataStartAtOrToday,
  selectBillingEntityDataEndAtOrToday,
  (billingEntityDataStartAtOrToday, billingEntityDataEndAtOrToday) => {
    return {
      startAtDate: format(billingEntityDataStartAtOrToday, "yyyy-MM-dd"),
      endAtDate: format(billingEntityDataEndAtOrToday, "yyyy-MM-dd"),
    };
  },
);

/**
 * @returns if start date is before new year date then return new year date or else
 */
const getStartAtDate = (startAt: string | undefined, year: number) => {
  if (!startAt) return undefined;
  const startAtDate = new Date(startAt);
  const yearStartDate = new Date(`${year}-01-01`);
  return isAfter(startAtDate, yearStartDate) ? startAtDate : yearStartDate;
};
/**
 * Select start and end dates for this year and last year for montly usage in usage page
 */
export const selectBillingDerivedForMonthlyRequest = createSelector(
  (state: RootState) => state.billingEntityData?.billingEntityData,
  (billingEntityData) => {
    const thisYear = new Date().getFullYear();
    const lastYear = thisYear - 1;

    const thisYearStartAt = getStartAtDate(
      billingEntityData?.start_at,
      thisYear,
    );
    const lastYearStartAt = getStartAtDate(
      billingEntityData?.start_at,
      lastYear,
    );
    const lastYearEndAt = new Date(`${lastYear}-12-31`);
    const thisYearEndAt = new Date(`${thisYear}-12-31`);

    return {
      thisYearStartAt,
      thisYearEndAt,
      lastYearStartAt,
      lastYearEndAt,
    };
  },
);
